import { TrendingUp, Trophy, Medal, Award, Crown, Star, Zap } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { Badge } from '@/components/ui/badge'

interface RankingProgressProps {
  level: number
  auraXP: number
  currentRealm: string
  consecutiveSubmissions?: number
  onViewRewards?: () => void
}

// AURA XP thresholds for each level
const XP_THRESHOLDS = [
  0, 500, 1500, 3500, 7000, 12000, 19500, 30000, 45000, 64500,      // Levels 1-10 (Challenger)
  89500, 119500, 154500, 194500, 239500, 289500, 344500, 404500, 469500, 539500,  // Levels 11-20 (Competitor) 
  614500, 694500, 779500, 869500, 964500, 1064500, 1169500, 1279500, 1394500, 1514500, // Levels 21-30 (Contender)
  1639500, 1769500, 1904500, 2044500, 2189500, 2339500, 2494500, 2654500, 2819500, 2989500, // Levels 31-40 (Champion)
  3164500, 3344500, 3529500, 3719500, 3914500, 4114500, 4319500, 4529500, 4744500, 4964500  // Levels 41-50 (Legend)
]

const REALM_CONFIG = {
  Challenger: { 
    icon: Star, 
    color: 'from-slate-500 to-slate-600',
    badgeVariant: 'secondary' as const,
    levels: '1-10'
  },
  Competitor: { 
    icon: Trophy, 
    color: 'from-amber-500 to-amber-600',
    badgeVariant: 'secondary' as const,
    levels: '11-20'
  },
  Contender: { 
    icon: Medal, 
    color: 'from-blue-500 to-blue-600',
    badgeVariant: 'default' as const,
    levels: '21-30'
  },
  Champion: { 
    icon: Award, 
    color: 'from-purple-500 to-purple-600',
    badgeVariant: 'default' as const,
    levels: '31-40'
  },
  Legend: { 
    icon: Crown, 
    color: 'from-yellow-500 to-yellow-600',
    badgeVariant: 'default' as const,
    levels: '41-50'
  },
  Echelon: { 
    icon: Zap, 
    color: 'from-gradient-primary',
    badgeVariant: 'default' as const,
    levels: '51+'
  }
}

function getNextLevelXP(level: number, auraXP: number): number {
  if (level <= 50 && XP_THRESHOLDS[level]) {
    return XP_THRESHOLDS[level]
  }
  // Echelon realm: Each level needs 250k+ XP
  const echelonLevelOffset = level - 50
  return 4964500 + (echelonLevelOffset * 250000)
}

export function RankingProgress({ 
  level = 1, 
  auraXP = 0, 
  currentRealm = 'Challenger',
  consecutiveSubmissions = 0,
  onViewRewards 
}: RankingProgressProps) {
  const nextLevelXP = getNextLevelXP(level, auraXP)
  const currentLevelXP = level > 1 && level <= 50 ? XP_THRESHOLDS[level - 1] : (level > 50 ? 4964500 + ((level - 51) * 250000) : 0)
  
  const progress = nextLevelXP > currentLevelXP 
    ? ((auraXP - currentLevelXP) / (nextLevelXP - currentLevelXP)) * 100 
    : 100
  
  const xpToNext = Math.max(0, nextLevelXP - auraXP)
  const realmConfig = REALM_CONFIG[currentRealm as keyof typeof REALM_CONFIG] || REALM_CONFIG.Challenger
  const RealmIcon = realmConfig.icon

  return (
    <Card className={`bg-gradient-to-br ${realmConfig.color}/10 to-background border border-primary/20`}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary" />
            AURA XP Progress
          </div>
          <Button 
            variant="outline" 
            size="sm"
            onClick={onViewRewards}
            className="border-primary/20 hover:bg-primary/10"
          >
            View Ledger
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Realm & Level Display */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg bg-gradient-to-br ${realmConfig.color}`}>
              <RealmIcon className="h-6 w-6 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <Badge variant={realmConfig.badgeVariant} className="font-semibold">
                  {currentRealm} Realm
                </Badge>
                <span className="text-xs text-muted-foreground">
                  (Levels {realmConfig.levels})
                </span>
              </div>
              <div className="text-2xl font-bold">Level {level}</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-sm text-muted-foreground">Total AURA XP</div>
            <div className="font-bold text-lg text-primary">{auraXP.toLocaleString()}</div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="space-y-2">
          <div className="flex justify-between items-center text-sm">
            <span className="text-muted-foreground">Level {level} Progress</span>
            <span className="font-medium">{xpToNext > 0 ? `${xpToNext.toLocaleString()} XP to go` : 'MAX LEVEL'}</span>
          </div>
          <Progress 
            value={Math.min(progress, 100)} 
            className="h-3"
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>{currentLevelXP.toLocaleString()} XP</span>
            <span>{Math.round(progress)}% complete</span>
            <span>{nextLevelXP.toLocaleString()} XP</span>
          </div>
        </div>

        {/* Consistency Bonus Progress */}
        {consecutiveSubmissions > 0 && (
          <div className="p-3 bg-gradient-to-r from-success/10 to-success/5 rounded-lg border border-success/20">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium text-success">Consistency Streak</span>
              <span className="text-success">
                {consecutiveSubmissions}/10 {consecutiveSubmissions >= 10 ? '✓' : ''}
              </span>
            </div>
            <Progress 
              value={(consecutiveSubmissions % 10) * 10} 
              className="h-2 mt-2" 
            />
            <p className="text-xs text-muted-foreground mt-1">
              +50 AXP bonus every 10 consecutive submissions
            </p>
          </div>
        )}

        {/* XP Sources */}
        <div className="text-xs text-muted-foreground space-y-1">
          <p className="font-medium">Earn AURA XP by:</p>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>• 1st Place: +500 AXP</div>
            <div>• Top 5%: +300 AXP</div>
            <div>• Top 10%: +200 AXP</div>
            <div>• Top 25%: +100 AXP</div>
            <div>• Top 50%: +50 AXP</div>
            <div>• Consistency: +50 AXP</div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}